'use client';

import { useMemo } from 'react';
import { useCurrencyStore } from '@/store/currencyStore';
import {
  convertAmountWithRates,
  getCurrencyCode,
} from '@/utils/currencyHelpers';

export function useConvertedPrice(
  amount: number,
  backendCurrency?: string,
  overrideRate?: number
) {
  const { currencyCode, targetCurrencyRate, exchangeRates, baseCurrency } =
    useCurrencyStore();

  const convertedPrice = useMemo(() => {
    const safeAmount =
      typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;

    const fromCurrency = backendCurrency
      ? getCurrencyCode(backendCurrency)
      : baseCurrency || 'USD';
    const toCurrency = currencyCode || baseCurrency || 'USD';

    let converted = convertAmountWithRates({
      amount: safeAmount,
      fromCurrency,
      toCurrency,
      exchangeRates,
      baseCurrency,
    });

    const currenciesDiffer = fromCurrency !== toCurrency;
    const needsFallback =
      currenciesDiffer && (Number.isNaN(converted) || converted === safeAmount);

    if (needsFallback) {
      const base = baseCurrency || 'USD';
      const rate =
        typeof overrideRate === 'number' && overrideRate > 0
          ? overrideRate
          : targetCurrencyRate;

      if (rate && rate > 0) {
        if (fromCurrency === base) {
          converted = safeAmount * rate;
        } else if (toCurrency === base) {
          converted = safeAmount / rate;
        } else {
          // Convert to base first, then to target using the available rate
          const amountInBase =
            fromCurrency === base ? safeAmount : safeAmount / rate;
          converted = toCurrency === base ? amountInBase : amountInBase * rate;
        }
      } else {
        converted = safeAmount;
      }
    }

    return Number(Number.isFinite(converted) ? converted.toFixed(2) : 0);
  }, [
    amount,
    backendCurrency,
    overrideRate,
    baseCurrency,
    currencyCode,
    exchangeRates,
    targetCurrencyRate,
  ]);

  return convertedPrice;
}
